import {
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  HorizontalOriginOption,
  VerticalOriginOption,
} from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { SvgDatasourceCertified14Px, SvgDatasource14Px, SvgTableCertified14Px, SvgTable14Px } from '../../../../../gen-src/icons';
import { sharedDialogTelemetry } from '../telemetry/SharedDialogTelemetry';
import {
  getTelemetryEnvironmentEnumValueFromString,
  TelemetryContext,
  TelemetryEnvironment
} from '../telemetry/TelemetryContext';
import { telemetrySender } from '../telemetry/TelemetrySender';
import {
  ColumnDescriptionCell,
  ColumnDescriptionFieldName,
  ColumnDescriptionInheritedColumnRow,
  ColumnDescriptionPane,
  ColumnDescriptionWrapper,
  DescriptionLabel,
  DescriptionText,
  dialogWidth,
  InheritedDetailsLabel,
  InheritedDetailsValue,
  InheritedRow,
  AssetParentIconStyle,
  DescriptionDialogBodyStyle,
} from './ColumnDescriptionDialogStyles';

export interface AssetParentProps {
  contentType: 'datasource' | 'table';
  isCertified: boolean;
  name: string;
}

export interface ColumnDescriptionDialogProps {
  readonly anchorEl: HTMLElement;
  readonly assetParent: AssetParentProps;
  readonly description: string | null;
  readonly fieldName: string;
  readonly inheritedColumnName: string | null;
  readonly siteLuid: string;
  readonly tableauVersion: string;
  readonly telemetryEnvironment: string;
  // todo afeyissa tfsid-1434748
  // investigate the effects of not using the dialogwidget wrapper for
  // non-hybrid contexts (eg WebAuth) and remove if not necessary.
  readonly isForHybridDialog?: boolean;
  // telemetry-contract-api requires this particular signature for its callback
  // eslint-disable-next-line @typescript-eslint/ban-types
  readonly telemetryContractApi?: (eventType: string, payload: {}[]) => void;
  readonly onClose: () => void;
}

export function InheritedRowDetails(props: {
  label: string | null;
  value: string | null;
  icon?: JSX.Element;
}): JSX.Element | null {
  if (props.value == null) {
    return null;
  }
  return (
    <InheritedRow>
      <InheritedDetailsLabel>
        {props.label} {props.icon || null}
      </InheritedDetailsLabel>

      <InheritedDetailsValue>{props.value}</InheritedDetailsValue>
    </InheritedRow>
  );
}

export class ColumnDescriptionDialog extends React.Component<
  ColumnDescriptionDialogProps
> {
  private displayStartTime: number = Date.now();
  private sentDialogClosedTelemetry = false;

  private sendDialogClosedTelemetry = (): void => {
    if (this.sentDialogClosedTelemetry) { return; }

    const displayEndTime: number = Date.now();
    const displayDuration = displayEndTime - this.displayStartTime;

    const typeOfDataDisplayed = 'column-description';

    const telemetryEnvironment: TelemetryEnvironment = getTelemetryEnvironmentEnumValueFromString(
      this.props.telemetryEnvironment);
    const dialogReleaseVersion: string = this.props.tableauVersion;
    const siteLuid: string = this.props.siteLuid;
    const telemetryContext: TelemetryContext = {
      telemetryEnvironment: telemetryEnvironment,
      dialogReleaseVersion: dialogReleaseVersion,
      siteLuid: siteLuid,
    };

    if (this.props.telemetryContractApi) {
      telemetrySender.setTelemetryContractApiCallback(this.props.telemetryContractApi);
      sharedDialogTelemetry.sendDataDisplayedInSharedDialog(typeOfDataDisplayed, displayDuration, telemetryContext);
    }

    this.sentDialogClosedTelemetry = true;
  }

  private onClose = (): void => {
    this.sendDialogClosedTelemetry();
    this.props.onClose();
  }

  public override componentWillUnmount(): void {
    this.sendDialogClosedTelemetry();
  }

  public override render(): JSX.Element {
    const shouldShowInheritedDetails =
      !!this.props.inheritedColumnName || !!this.props.assetParent;
    const dialogTitle = `${Messages.ColumnDescription()}: ${this.props.fieldName}`;

    const content = <ColumnDescriptionPane>
      <ColumnDescriptionFieldName>
        {this.props.fieldName}
      </ColumnDescriptionFieldName>
      <ColumnDescriptionCell>
        <DescriptionLabel>{Messages.Description()}</DescriptionLabel>
        <DescriptionText>{this.props.description}</DescriptionText>
      </ColumnDescriptionCell>
      {shouldShowInheritedDetails && this.renderInheritanceDetails()}
    </ColumnDescriptionPane>;

    if (this.props.isForHybridDialog) {
      return <ColumnDescriptionWrapper>
        {content}
      </ColumnDescriptionWrapper>;
    }

    // todo afeyissa tfsid-1434748
    // investigate the effects of not using the dialogwidget wrapper for
    // non-hybrid contexts (eg WebAuth) and remove if not necessary.
    return (
      <DialogWidget
        anchorEl={this.props.anchorEl}
        anchorOrigin={{
          vertical: VerticalOriginOption.top,
          horizontal: HorizontalOriginOption.right,
        }}
        dialogBodyStyle={DescriptionDialogBodyStyle}
        dialogWidth={dialogWidth}
        disableAutoFocus={true}
        isModal={false}
        onCloseRequest={this.onClose}
        positioningBehavior={DialogPositioningBehavior.Anchored}
        showCloseButton={false}
        showFooter={false}
        testId='column-description'
        title={dialogTitle}
        type={DialogType.Uber}
      >
        {content}
      </DialogWidget>
    );
  }

  private renderInheritanceDetails(): React.ReactNode {
    return (
      <ColumnDescriptionCell>
        {!!this.props.inheritedColumnName && (
          <ColumnDescriptionInheritedColumnRow>
            <InheritedRowDetails
              label={Messages.DescriptionInheritanceColumnLabel()}
              value={this.props.inheritedColumnName}
            />
          </ColumnDescriptionInheritedColumnRow>
        )}
        {!!this.props.assetParent && this.renderAssetParent()}
      </ColumnDescriptionCell>
    );
  }

  private renderAssetParent(): React.ReactNode {
    let label: string | null = null;
    let icon: JSX.Element;

    switch (this.props.assetParent.contentType) {
      case 'datasource':
        label = Messages.DescriptionInheritanceDatasourceLabel();
        icon = this.props.assetParent.isCertified ? (
          <SvgDatasourceCertified14Px
            style={{
              padding: '0 3px',
              verticalAlign: 'top',
            }}
          />
        ) : (
          <SvgDatasource14Px
            style={{
              padding: '0 3px',
              verticalAlign: 'top',
            }}
          />
        );
        break;

      case 'table':
        label = Messages.DescriptionInheritanceTableLabel();
        icon = this.props.assetParent.isCertified ? (
          <SvgTableCertified14Px style={AssetParentIconStyle} />
        ) : (
          <SvgTable14Px style={AssetParentIconStyle} />
        );
        break;

      default:
        return;
    }

    return (
      <ColumnDescriptionInheritedColumnRow>
        <InheritedRowDetails
          label={label}
          value={this.props.assetParent.name}
          icon={icon}
        />
      </ColumnDescriptionInheritedColumnRow>
    );
  }
}
